import React, { useEffect, useState, useCallback } from "react";
import { useApp } from "../context/AppContext";
import { Card, SectionLabel, Button, Input, Select } from "../components/ui";
import api from "../lib/api";
import { Users, Plus, Trash2, Copy, X, Mail, SlidersHorizontal } from "lucide-react";

const ROLE_COLOR = {
  owner: "#34D399", super_admin: "#A78BFA", admin: "#60A5FA",
  manager: "#22D3EE", member: "#FBBF24", viewer: "#A1A1AA",
};
const RANK = (r) => ({ viewer: 1, member: 2, manager: 3, admin: 4, super_admin: 5, owner: 6 }[r] || 0);

// Roles a manager may assign are bounded server-side; this is the display order.
const ASSIGNABLE = ["viewer", "member", "manager", "admin", "super_admin"];
const scopeForRole = (role) =>
  role === "super_admin" ? "group" : (role === "admin" || role === "manager") ? "entities" : "spaces";

function RolePill({ role, t }) {
  return (
    <span className="text-[11px] px-2 py-0.5 rounded-full font-medium capitalize"
      style={{ backgroundColor: `${ROLE_COLOR[role]}22`, color: ROLE_COLOR[role] }}>
      {t(`role_${role}`)}
    </span>
  );
}

// Owner/admin team management: members, invites, roles, entity- or space-level scope.
export default function TeamCard() {
  const { t, entities, canManageTeam, activeWorkspace, toast, loadWorkspaces } = useApp();
  const [data, setData] = useState(null);
  const [spaces, setSpaces] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [editMember, setEditMember] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = activeWorkspace?.id ? `?workspace_id=${activeWorkspace.id}` : "";
      const res = await api.get(`/team/members${params}`);
      setData(res.data);
    } catch {
      setData(null);
    }
    try { const r = await api.get("/spaces"); setSpaces(r.data || []); } catch { setSpaces([]); }
    try { const r = await api.get("/space-projects"); setProjects(r.data || []); } catch { setProjects([]); }
  }, [activeWorkspace?.id]);

  useEffect(() => { if (canManageTeam) load(); }, [canManageTeam, load]);

  if (!canManageTeam) return null;

  const myRole = data?.my_role || "owner";
  const roleOptions = ASSIGNABLE.filter((r) => myRole === "owner" || RANK(r) < RANK(myRole));

  const removeMember = async (uid) => {
    const { data: res } = await api.delete(`/team/members/${uid}${activeWorkspace?.id ? `?workspace_id=${activeWorkspace.id}` : ""}`);
    const p = res?.purged || {};
    const cleaned = (p.watchers || 0) + (p.notifications || 0) + (p.updates || 0) + (p.activity || 0) + (p.files || 0);
    toast(cleaned > 0
      ? `${t("team_member_removed")} · ${t("team_purged_count").replace("{n}", cleaned)}`
      : t("team_member_removed"));
    load();
  };
  const changeRole = async (uid, role) => {
    await api.patch(`/team/members/${uid}`, { role, workspace_id: activeWorkspace?.id });
    toast(t("team_role_updated"));
    load();
  };
  const saveAccess = async (uid, role, entityIds, spaceIds, projectIds, includeOperational) => {
    await api.patch(`/team/members/${uid}`, {
      role, entity_ids: entityIds, space_ids: spaceIds, project_ids: projectIds,
      include_operational: includeOperational, workspace_id: activeWorkspace?.id,
    });
    toast(t("team_access_updated"));
    setEditMember(null);
    load();
  };
  const revokeInvite = async (id) => {
    await api.delete(`/team/invite/${id}`);
    toast(t("team_invite_revoked"));
    load();
  };

  const entityName = (id) => (data?.entity_names?.[id]) || entities.find((e) => e.id === id)?.name || id;
  const spaceName = (id) => (data?.space_names?.[id]) || spaces.find((s) => s.id === id)?.name || id;
  const projectName = (id) => (data?.project_names?.[id]) || projects.find((p) => p.id === id)?.name || id;
  const scopeLabel = (x) => {
    const sc = x.scope || scopeForRole(x.role);
    if (sc === "group") return x.is_owner ? "" : t("invite_whole_group");
    if (sc === "entities") return (x.entity_ids || []).map(entityName).join(", ");
    const sp = (x.space_ids || []).map(spaceName).join(", ");
    const pr = (x.project_ids || []).map(projectName);
    return pr.length ? `${sp} · ${pr.join(", ")}` : sp;
  };

  return (
    <Card className="mb-6" data-testid="team-card">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-text-secondary" />
          <SectionLabel className="!mb-0">{t("team_title")}</SectionLabel>
        </div>
        <Button onClick={() => setShowInvite(true)} data-testid="team-invite-button" className="shrink-0">
          <Plus className="w-4 h-4 mr-1.5" /> {t("team_invite")}
        </Button>
      </div>
      <p className="text-text-secondary text-sm mb-4">{t("team_subtitle")}</p>

      {!data?.email_configured && (
        <p className="text-[11px] text-text-secondary mb-3">{t("team_email_note")}</p>
      )}

      <div className="space-y-2" data-testid="team-members">
        {(data?.members || []).map((m) => (
          <div key={m.user_id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl border" data-testid={`team-member-${m.user_id}`}>
            <div className="w-8 h-8 rounded-full bg-surface-hover flex items-center justify-center text-xs font-semibold shrink-0">
              {(m.name || m.email || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{m.name || m.email}</div>
              <div className="text-[11px] text-text-secondary truncate">
                {m.email}
                {!m.is_owner && scopeLabel(m) && ` · ${scopeLabel(m)}`}
              </div>
            </div>
            {m.is_owner ? (
              <RolePill role="owner" t={t} />
            ) : (
              <>
                <Select value={m.role} onChange={(e) => changeRole(m.user_id, e.target.value)}
                  data-testid={`team-role-${m.user_id}`} className="max-w-[130px]">
                  {roleOptions.map((r) => <option key={r} value={r}>{t(`role_${r}`)}</option>)}
                </Select>
                <button onClick={() => setEditMember(m)} data-testid={`team-access-${m.user_id}`}
                  title={t("team_manage_access")}
                  className="text-text-secondary hover:text-text-primary transition-colors">
                  <SlidersHorizontal className="w-4 h-4" />
                </button>
                <button onClick={() => removeMember(m.user_id)} data-testid={`team-remove-${m.user_id}`}
                  className="text-text-secondary hover:text-red-400 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        ))}
      </div>

      {(data?.invites || []).length > 0 && (
        <div className="mt-4">
          <div className="text-[11px] uppercase tracking-wide text-text-secondary mb-2">{t("team_pending")}</div>
          <div className="space-y-2" data-testid="team-invites">
            {data.invites.map((inv) => (
              <div key={inv.id} className="flex items-center gap-3 px-3 py-2 rounded-xl border border-dashed" data-testid={`team-invite-${inv.id}`}>
                <Mail className="w-4 h-4 text-text-secondary shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate">{inv.email}</div>
                  <div className="text-[11px] text-text-secondary truncate">{scopeLabel(inv)}</div>
                </div>
                <RolePill role={inv.role} t={t} />
                <button onClick={() => revokeInvite(inv.id)} data-testid={`team-revoke-${inv.id}`}
                  className="text-text-secondary hover:text-red-400 transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {showInvite && (
        <InviteDialog
          entities={entities}
          spaces={spaces}
          projects={projects}
          roleOptions={roleOptions}
          workspaceId={activeWorkspace?.id}
          onClose={() => setShowInvite(false)}
          onDone={() => { setShowInvite(false); load(); loadWorkspaces(); }}
        />
      )}

      {editMember && (
        <AccessDialog
          member={editMember}
          entities={entities}
          spaces={spaces}
          projects={projects}
          roleOptions={roleOptions}
          onClose={() => setEditMember(null)}
          onSave={(role, eids, sids, pids, op) => saveAccess(editMember.user_id, role, eids, sids, pids, op)}
        />
      )}
    </Card>
  );
}

// Role-aware scope picker: entities for admin/manager, spaces+projects (grouped) for
// member/viewer with an optional operational-access toggle, nothing for super_admin.
function ScopePicker({ role, entities, spaces, projects, entityIds, setEntityIds,
  spaceIds, setSpaceIds, projectIds, setProjectIds, includeOperational, setIncludeOperational, idPrefix }) {
  const { t } = useApp();
  const scope = scopeForRole(role);
  const toggle = (arr, setArr, id) =>
    setArr(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  if (scope === "group") {
    return <p className="text-xs text-text-secondary px-1 py-2" data-testid={`${idPrefix}-scope-group`}>{t("scope_super_admin_note")}</p>;
  }
  if (scope === "entities") {
    return (
      <div>
        <label className="text-xs text-text-secondary block mb-1.5">{t("invite_specific_entities")}</label>
        <div className="flex flex-wrap gap-1.5" data-testid={`${idPrefix}-entity-picker`}>
          {entities.map((e) => (
            <button key={e.id} onClick={() => toggle(entityIds, setEntityIds, e.id)} data-testid={`${idPrefix}-entity-${e.id}`}
              className={`px-2.5 py-1 rounded-full border text-xs transition-all ${entityIds.includes(e.id) ? "text-text-primary" : "text-text-secondary"}`}
              style={entityIds.includes(e.id) ? { borderColor: e.color, backgroundColor: `${e.color}1a` } : {}}>
              {e.name}
            </button>
          ))}
        </div>
      </div>
    );
  }
  // spaces — grouped by entity, with an optional project narrowing + operational toggle
  const visibleProjects = projects.filter((p) => spaceIds.includes(p.space_id));
  const allProjectsSelected = visibleProjects.length > 0 && visibleProjects.every((p) => projectIds.includes(p.id));
  const toggleAllProjects = () => {
    const visIds = visibleProjects.map((p) => p.id);
    setProjectIds(allProjectsSelected
      ? projectIds.filter((id) => !visIds.includes(id))
      : [...new Set([...projectIds, ...visIds])]);
  };
  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs text-text-secondary block mb-1.5">{t("invite_specific_spaces")}</label>
        <div className="space-y-2.5 max-h-48 overflow-y-auto" data-testid={`${idPrefix}-space-picker`}>
          {entities.map((e) => {
            const es = spaces.filter((s) => s.entity_id === e.id);
            if (!es.length) return null;
            return (
              <div key={e.id}>
                <div className="text-[10px] uppercase tracking-wide text-text-secondary mb-1 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: e.color }} />{e.name}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {es.map((s) => (
                    <button key={s.id} onClick={() => toggle(spaceIds, setSpaceIds, s.id)} data-testid={`${idPrefix}-space-${s.id}`}
                      className={`px-2.5 py-1 rounded-full border text-xs transition-all ${spaceIds.includes(s.id) ? "text-text-primary" : "text-text-secondary"}`}
                      style={spaceIds.includes(s.id) ? { borderColor: e.color, backgroundColor: `${e.color}1a` } : {}}>
                      {s.name}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
          {!spaces.length && <p className="text-xs text-text-secondary">{t("scope_no_spaces")}</p>}
        </div>
      </div>

      {spaceIds.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-xs text-text-secondary">{t("invite_specific_projects")}</label>
            {visibleProjects.length > 0 && (
              <button onClick={toggleAllProjects} data-testid={`${idPrefix}-project-select-all`}
                className="text-[11px] text-primary hover:underline">
                {allProjectsSelected ? t("clear_all") : t("select_all")}
              </button>
            )}
          </div>
          <p className="text-[10px] text-text-secondary mb-1.5">{t("invite_projects_hint")}</p>
          <div className="flex flex-wrap gap-1.5" data-testid={`${idPrefix}-project-picker`}>
            {visibleProjects.map((p) => (
              <button key={p.id} onClick={() => toggle(projectIds, setProjectIds, p.id)} data-testid={`${idPrefix}-project-${p.id}`}
                className={`px-2.5 py-1 rounded-full border text-xs transition-all ${projectIds.includes(p.id) ? "border-primary text-text-primary bg-primary/10" : "text-text-secondary"}`}>
                {p.name}
              </button>
            ))}
            {visibleProjects.length === 0 &&
              <p className="text-xs text-text-secondary">{t("invite_no_projects")}</p>}
          </div>
        </div>
      )}

      <button onClick={() => setIncludeOperational(!includeOperational)} role="switch" aria-checked={includeOperational}
        data-testid={`${idPrefix}-operational-toggle`}
        className="flex items-center justify-between w-full px-3 py-2 rounded-lg border text-xs hover:bg-surface-hover transition-colors">
        <span className="text-text-primary text-left">{t("invite_operational")}<span className="block text-[10px] text-text-secondary">{t("invite_operational_hint")}</span></span>
        <span className="relative w-9 h-5 rounded-full transition-colors shrink-0" style={{ backgroundColor: includeOperational ? "#2563EB" : "var(--border)" }}>
          <span className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all" style={{ left: includeOperational ? "18px" : "2px" }} />
        </span>
      </button>
    </div>
  );
}

function AccessDialog({ member, entities, spaces, projects, roleOptions, onClose, onSave }) {
  const { t } = useApp();
  const [role, setRole] = useState(member.role);
  const [entityIds, setEntityIds] = useState(member.entity_ids || []);
  const [spaceIds, setSpaceIds] = useState(member.space_ids || []);
  const [projectIds, setProjectIds] = useState(member.project_ids || []);
  const [includeOperational, setIncludeOperational] = useState(!!member.include_operational);
  const [busy, setBusy] = useState(false);
  const scope = scopeForRole(role);
  const invalid = (scope === "entities" && entityIds.length === 0) || (scope === "spaces" && spaceIds.length === 0);

  const submit = async () => {
    if (invalid) return;
    setBusy(true);
    try {
      await onSave(role, scope === "entities" ? entityIds : [],
        scope === "spaces" ? spaceIds : [], scope === "spaces" ? projectIds : [],
        scope === "spaces" ? includeOperational : false);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4" onMouseDown={onClose}>
      <div className="w-full max-w-md rounded-2xl border bg-surface p-5" onMouseDown={(e) => e.stopPropagation()} data-testid="access-dialog">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-heading font-medium">{t("team_manage_access")}</h3>
          <button onClick={onClose} className="text-text-secondary hover:text-text-primary"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-text-secondary mb-4 truncate">{member.name || member.email}</p>

        <div className="space-y-3">
          <div>
            <label className="text-xs text-text-secondary block mb-1">{t("invite_role")}</label>
            <Select value={role} onChange={(e) => setRole(e.target.value)} data-testid="access-role-select">
              {roleOptions.map((r) => <option key={r} value={r}>{t(`role_${r}`)}</option>)}
            </Select>
          </div>
          <ScopePicker role={role} entities={entities} spaces={spaces} projects={projects}
            entityIds={entityIds} setEntityIds={setEntityIds} spaceIds={spaceIds} setSpaceIds={setSpaceIds}
            projectIds={projectIds} setProjectIds={setProjectIds}
            includeOperational={includeOperational} setIncludeOperational={setIncludeOperational} idPrefix="access" />
          <div className="flex gap-2 pt-1">
            <Button variant="outline" className="flex-1" onClick={onClose} data-testid="access-cancel">{t("cancel")}</Button>
            <Button className="flex-1" onClick={submit} disabled={busy || invalid} data-testid="access-save">
              {busy ? t("loading") : t("save")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function InviteDialog({ entities, spaces, projects, roleOptions, workspaceId, onClose, onDone }) {
  const { t, toast } = useApp();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [entityIds, setEntityIds] = useState([]);
  const [spaceIds, setSpaceIds] = useState([]);
  const [projectIds, setProjectIds] = useState([]);
  const [includeOperational, setIncludeOperational] = useState(false);
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState("");
  const scope = scopeForRole(role);
  const invalid = (scope === "entities" && entityIds.length === 0) || (scope === "spaces" && spaceIds.length === 0);

  const submit = async () => {
    if (!email.trim()) return;
    if (invalid) { toast(scope === "spaces" ? t("invite_pick_space") : t("invite_pick_entity")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/team/invite", {
        email: email.trim(), role, entity_ids: entityIds, space_ids: spaceIds,
        project_ids: scope === "spaces" ? projectIds : [],
        include_operational: scope === "spaces" ? includeOperational : false,
        workspace_id: workspaceId, origin: window.location.origin,
      });
      setLink(data.accept_url || "");
      toast(data.sent ? `${t("invite_sent_to")} ${email}` : t("invite_link_ready"));
      if (data.sent) onDone();
    } catch (e) {
      toast(e?.response?.data?.detail || t("digest_error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4" onMouseDown={onClose}>
      <div className="w-full max-w-md rounded-2xl border bg-surface p-5" onMouseDown={(e) => e.stopPropagation()} data-testid="invite-dialog">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-medium">{t("team_invite")}</h3>
          <button onClick={onClose} className="text-text-secondary hover:text-text-primary"><X className="w-4 h-4" /></button>
        </div>

        {link ? (
          <div data-testid="invite-link-box">
            <p className="text-sm text-text-secondary mb-2">{t("invite_link_ready")}</p>
            <div className="flex gap-2">
              <Input value={link} readOnly className="flex-1 text-xs" data-testid="invite-link-value" />
              <Button onClick={() => { navigator.clipboard?.writeText(link); toast(t("invite_link_copied")); }} data-testid="invite-copy-link">
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <Button className="w-full mt-4" onClick={onDone}>{t("invite_done")}</Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-xs text-text-secondary block mb-1">{t("invite_email")}</label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="teammate@example.com" data-testid="invite-email-input" />
            </div>
            <div>
              <label className="text-xs text-text-secondary block mb-1">{t("invite_role")}</label>
              <Select value={role} onChange={(e) => setRole(e.target.value)} data-testid="invite-role-select">
                {roleOptions.map((r) => <option key={r} value={r}>{t(`role_${r}`)}</option>)}
              </Select>
            </div>
            <ScopePicker role={role} entities={entities} spaces={spaces} projects={projects}
              entityIds={entityIds} setEntityIds={setEntityIds} spaceIds={spaceIds} setSpaceIds={setSpaceIds}
              projectIds={projectIds} setProjectIds={setProjectIds}
              includeOperational={includeOperational} setIncludeOperational={setIncludeOperational} idPrefix="invite" />
            <Button className="w-full" onClick={submit} disabled={busy} data-testid="invite-submit">
              <Mail className="w-4 h-4 mr-2" /> {busy ? t("loading") : t("invite_send")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
